import { useRef, useState, useEffect } from 'react';

const STATS = [
  { num: '25+', label: 'Years in Business', icon: 'ri-time-line' },
  { num: '100%', label: 'USA Built Systems', icon: 'ri-flag-line' },
  { num: '50+', label: 'System Configurations', icon: 'ri-settings-3-line' },
  { num: '1,000s', label: 'Systems Delivered', icon: 'ri-truck-line' },
];

const LOCATIONS = [
  { city: 'Kansas City, MO', type: 'primary' },
  { city: 'Overland Park, KS', type: 'primary' },
  { city: 'Prairie Village, KS', type: 'home' },
  { city: 'Lenexa, KS', type: 'secondary' },
  { city: 'Olathe, KS', type: 'secondary' },
  { city: "Lee's Summit, MO", type: 'secondary' },
  { city: 'Independence, MO', type: 'secondary' },
  { city: 'Blue Springs, MO', type: 'secondary' },
  { city: 'Shawnee, KS', type: 'secondary' },
  { city: 'Merriam, KS', type: 'secondary' },
  { city: 'Kansas City Metro', type: 'region' },
  { city: 'Midwest Region', type: 'region' },
  { city: 'Nationwide USA', type: 'region' },
  { city: 'International Shipping', type: 'region' },
];

const SEO_PHRASES = [
  'Industrial system services in Kansas City',
  'Process system installation near Kansas City',
  'Quality system experts in the Midwest',
  'Parts washers Kansas City MO',
  'Industrial cleaning equipment Prairie Village KS',
  'Custom parts washer Kansas City',
  'MAXJET dealer Kansas City',
];

export default function ServiceAreas() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative bg-[#060606] py-32 overflow-hidden">

      {/* Top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />

      <div className="max-w-7xl mx-auto px-8 lg:px-12">

        {/* Header */}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-3 text-[#c9a84c] text-[10px] font-bold tracking-[0.5em] uppercase font-sans">
              <span className="h-px w-8 bg-[#c9a84c]" />
              Coverage &amp; Service Areas
            </span>
            <h2 className="font-serif text-5xl text-white mt-5 mb-6 leading-tight">
              Serving Kansas City<br />
              <em className="not-italic text-[#c9a84c]">and the Nation</em>
            </h2>
            <p className="text-white/40 font-sans text-base leading-relaxed mb-8">
              Industrial system services in Kansas City, serving commercial and industrial markets across the entire Midwest and beyond. Our American-built MAXJET equipment ships USA-to-worldwide with full installation support.
            </p>

            {/* Location tags */}
            <div className="flex flex-wrap gap-2">
              {LOCATIONS.map((loc, i) => (
                <span
                  key={i}
                  className={`px-3.5 py-1.5 rounded-full font-sans text-xs whitespace-nowrap cursor-default transition-colors ${
                    loc.type === 'home'
                      ? 'border border-[#c9a84c]/40 text-[#c9a84c] bg-[#c9a84c]/5'
                      : loc.type === 'primary'
                      ? 'border border-[#2a2a2a] text-white/50 hover:border-[#c9a84c]/25 hover:text-white/70'
                      : loc.type === 'region'
                      ? 'border border-[#1c1c1c] text-white/25 italic'
                      : 'border border-[#1c1c1c] text-white/35 hover:border-[#2a2a2a]'
                  }`}
                >
                  {loc.type === 'home' && <i className="ri-map-pin-fill mr-1.5 text-[10px]" />}
                  {loc.city}
                </span>
              ))}
            </div>
          </div>

          {/* Stats cards — 4 grid */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {STATS.map((stat, i) => (
              <div
                key={i}
                className={`group relative flex items-center gap-5 px-7 py-8 rounded-xl border border-[#1c1c1c] hover:border-[#c9a84c]/25 bg-[#0d0d0d] hover:bg-[#111] transition-all duration-400 overflow-hidden ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${200 + i * 80}ms` }}
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#c9a84c]/4 rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-xl bg-[#c9a84c]/8 border border-[#c9a84c]/15 group-hover:bg-[#c9a84c]/14 transition-colors">
                  <i className={`${stat.icon} text-[#c9a84c] text-lg`} />
                </div>
                <div>
                  <div className="font-serif text-3xl text-[#c9a84c] font-bold leading-none mb-1">{stat.num}</div>
                  <p className="text-white/40 text-xs font-sans tracking-wide">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map embed */}
        <div
          className={`rounded-xl overflow-hidden border border-[#1c1c1c] transition-all duration-700 delay-400 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="bg-[#0d0d0d] border-b border-[#1c1c1c] px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#c9a84c]/8 border border-[#c9a84c]/15">
                <i className="ri-map-pin-line text-[#c9a84c] text-sm" />
              </div>
              <div>
                <p className="text-white/70 text-sm font-sans font-medium">KC Quality Systems</p>
                <p className="text-white/30 text-xs font-sans">Prairie Village, KS &mdash; Serving Kansas City &amp; Nationwide</p>
              </div>
            </div>
            <a
              href="https://maps.google.com/?q=Prairie+Village,+KS"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 text-[#c9a84c]/60 text-xs font-sans hover:text-[#c9a84c] transition-colors whitespace-nowrap"
            >
              Open in Maps <i className="ri-external-link-line" />
            </a>
          </div>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d99411.3534219!2d-94.70985!3d38.9919!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87c0ebb38e3da2bd%3A0xa2db90b00e0594d2!2sPrairie%20Village%2C%20KS!5e0!3m2!1sen!2sus!4v1711000000000"
            width="100%"
            height="420"
            style={{ border: 0, filter: 'invert(88%) hue-rotate(180deg) saturate(0.2) brightness(0.82)' }}
            allowFullScreen
            loading="lazy"
            title="KC Quality Systems Location — Prairie Village, KS &mdash; Industrial System Services Kansas City"
          />
        </div>

        {/* SEO phrase cloud */}
        <div className={`mt-8 transition-all duration-700 delay-600 ${inView ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-wrap justify-center gap-3">
            {SEO_PHRASES.map((text, i) => (
              <span
                key={i}
                className="px-3 py-1 bg-[#0d0d0d] border border-[#1a1a1a] rounded-full text-white/15 text-[10px] font-sans whitespace-nowrap"
              >
                {text}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/15 to-transparent" />
    </section>
  );
}
